import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Prende o foco dentro de um contêiner enquanto ele estiver aberto.
 *
 * Faz as tres coisas que uma sobreposicao acessivel precisa e que sao facilmente
 * esquecidas: move o foco para dentro ao abrir, cicla o `Tab` sem escapar, e
 * **devolve o foco ao elemento que abriu** ao fechar.
 *
 * @param ref Contêiner da sobreposicao.
 * @param active Se a armadilha esta ativa.
 * @param onEscape Chamado quando o usuario pressiona `Escape`.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape?: () => void,
): void {
  // `onEscape` fica numa referencia, e **fora** das dependencias do efeito.
  //
  // Quem chama costuma passar uma funcao criada no render. Com ela na lista de
  // dependencias, o efeito refaz a armadilha a cada render — e a armadilha
  // comeca movendo o foco para o primeiro elemento interativo. O resultado e
  // que digitar num campo do modal manda o foco embora na primeira tecla.
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return;

    const container = ref.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );

    // O foco vai para o primeiro elemento interativo, ou para o proprio
    // contêiner quando nao houver nenhum.
    (focusables()[0] ?? container).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        escapeRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const elements = focusables();
      if (elements.length === 0) {
        event.preventDefault();
        return;
      }

      const first = elements[0]!;
      const last = elements[elements.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [ref, active]);
}

/** Impede a rolagem do `<body>` enquanto a sobreposicao estiver aberta. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}
